/** Helpers for recognising and embedding YouTube links used as URL stimuli. */

/** Extract the 11-char video id from any common YouTube URL shape, else null. */
export function youTubeId(url: string | undefined | null): string | null {
  if (!url) return null
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/,
  )
  return m ? m[1] : null
}

export function isYouTubeUrl(url: string | undefined | null): boolean {
  return youTubeId(url) !== null
}

/** Thumbnail image for a YouTube video (used in wizard previews). */
export function youTubeThumb(id: string): string {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`
}

/** Full-res (1280×720) thumbnail. Not generated for every video — load with an
 *  onError fallback to youTubeThumb(). */
export function youTubeThumbMaxRes(id: string): string {
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`
}

/** Privacy-friendly embed URL. Pass extra player params via `params`. */
export function youTubeEmbed(id: string, params: Record<string, string | number> = {}): string {
  const q = new URLSearchParams(
    Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
      acc[k] = String(v)
      return acc
    }, {}),
  ).toString()
  return `https://www.youtube-nocookie.com/embed/${id}${q ? `?${q}` : ''}`
}

// ─────────────────────── Reading a video's length up front ───────────────────
//
// YouTube publishes no duration through oEmbed, so the only way to know how
// long a pasted link runs — before the customer builds a whole study around it
// — is to boot the IFrame player API and ask it. The player is created
// off-screen, read once, and destroyed.

export type YouTubePlayer = {
  getDuration: () => number
  destroy: () => void
  playVideo?: () => void
  pauseVideo?: () => void
  seekTo?: (seconds: number, allowSeekAhead: boolean) => void
  getCurrentTime?: () => number
  mute?: () => void
  unMute?: () => void
  setVolume?: (volume: number) => void
}

/** `YT.PlayerState` values the players react to. */
export const YT_STATE = { ENDED: 0, PLAYING: 1, PAUSED: 2, BUFFERING: 3, CUED: 5 } as const

export type YouTubeApi = {
  Player: new (
    host: HTMLElement,
    options: {
      videoId: string
      host?: string
      playerVars?: Record<string, string | number>
      events?: {
        onReady?: (event: { target: YouTubePlayer }) => void
        onError?: (event: { data: number }) => void
        onStateChange?: (event: { data: number; target: YouTubePlayer }) => void
      }
    },
  ) => YouTubePlayer
}

type YouTubeGlobals = {
  YT?: YouTubeApi
  onYouTubeIframeAPIReady?: () => void
}

const youTubeGlobals = () => window as unknown as Window & YouTubeGlobals

/** In-flight (or settled) load of the IFrame API script; the API is global and
 *  must only be injected once, however many links get pasted. */
let iframeApiPromise: Promise<YouTubeApi> | null = null

export function loadIframeApi(): Promise<YouTubeApi> {
  const w = youTubeGlobals()
  if (w.YT?.Player) return Promise.resolve(w.YT)
  if (iframeApiPromise) return iframeApiPromise

  const promise = new Promise<YouTubeApi>((resolve, reject) => {
    // Another script may already own this callback — chain rather than clobber.
    const previous = w.onYouTubeIframeAPIReady
    w.onYouTubeIframeAPIReady = () => {
      previous?.()
      if (w.YT?.Player) resolve(w.YT)
      else reject(new Error('YouTube IFrame API loaded without a player'))
    }
    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    script.onerror = () => reject(new Error('YouTube IFrame API could not be loaded'))
    document.head.appendChild(script)
  })
  // A blocked or failed load must not poison every later paste, so forget the
  // rejected promise and let the next attempt retry from scratch.
  iframeApiPromise = promise
  promise.catch(() => {
    if (iframeApiPromise === promise) iframeApiPromise = null
  })
  return promise
}

/**
 * Length of a YouTube video in seconds, or null when it cannot be determined
 * (private/removed video, blocked script, embedding disabled, slow network).
 * Never throws: the caller treats an unknown length as "let it through and let
 * the backend decide" rather than blocking a study on our own uncertainty.
 */
export async function youTubeDurationSeconds(videoId: string, timeoutMs = 10000): Promise<number | null> {
  let api: YouTubeApi
  try {
    api = await loadIframeApi()
  } catch {
    return null
  }

  return new Promise<number | null>((resolve) => {
    const host = document.createElement('div')
    // Off-screen rather than display:none — a hidden player never reports ready.
    host.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;'
    document.body.appendChild(host)

    let player: YouTubePlayer | null = null
    let done = false
    const finish = (seconds: number | null) => {
      if (done) return
      done = true
      window.clearTimeout(timer)
      try {
        player?.destroy()
      } catch {
        /* the player may already have torn itself down */
      }
      host.remove()
      resolve(seconds)
    }

    const timer = window.setTimeout(() => finish(null), timeoutMs)

    try {
      player = new api.Player(host, {
        videoId,
        host: 'https://www.youtube-nocookie.com',
        playerVars: { autoplay: 0, controls: 0, mute: 1 },
        events: {
          onReady: (event) => {
            const seconds = event.target.getDuration()
            // Live streams and not-yet-loaded metadata both report 0.
            finish(Number.isFinite(seconds) && seconds > 0 ? seconds : null)
          },
          onError: () => finish(null),
        },
      })
    } catch {
      finish(null)
    }
  })
}
